import { createClient } from '@supabase/supabase-js'
import { devLog } from './dev-logger'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

// Debug log
devLog.info('Environment variables:', {
  url: supabaseUrl ? '✓ URL loaded' : '✗ URL missing',
  key: supabaseAnonKey ? '✓ Key loaded' : '✗ Key missing'
});

if (!supabaseUrl || !supabaseAnonKey) {
  devLog.error('Missing Supabase environment variables! Check .env file.')
}

// Singleton guarantee + single client (prevents infinite loading)
const globalAny = globalThis as any
export const supabase =
  globalAny.__sb__ ?? (globalAny.__sb__ = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Skip email confirmation in development
    ...(process.env.NODE_ENV === 'development' && {
      flowType: 'pkce'
    })
  },
  global: {
    headers: {
      'X-Client-Info': 'liberacure@1.0.0',
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 2, // Rate limit real-time events
    },
  }
}))

// Database types
export interface Database {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string
          username: string | null
          name: string
          phone: string | null
          grade: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          username?: string | null
          name: string
          phone?: string | null
          grade?: string
        }
        Update: {
          username?: string | null
          name?: string
          phone?: string | null
          grade?: string
        }
      }
      products: {
        Row: {
          id: string
          name: string
          manufacturer: string
          categories: string[]
          price: number
          original_price: number
          pill_quantity: number
          description: string
          ingredients: string
          usage_instructions: string
          warnings: string
          storage_instructions: string
          image: string | null
          stock: number
          is_active: boolean
          rating: number
          review_count: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          name: string
          manufacturer: string
          categories: string[]
          price: number
          original_price: number
          pill_quantity: number
          description: string
          ingredients: string
          usage_instructions: string
          warnings: string
          storage_instructions: string
          image?: string | null
          stock?: number
          is_active?: boolean
        }
        Update: {
          name?: string
          manufacturer?: string
          categories?: string[]
          price?: number
          original_price?: number
          pill_quantity?: number
          description?: string
          ingredients?: string
          usage_instructions?: string
          warnings?: string
          storage_instructions?: string
          image?: string | null
          stock?: number
          is_active?: boolean
        }
      }
      orders: {
        Row: {
          id: string
          user_id: string
          user_name: string
          user_email: string
          user_phone: string | null
          order_number: string
          subtotal: number
          shipping_fee: number
          discount: number
          points_used: number
          total_amount: number
          coupon_id: string | null
          coupon_code: string | null
          status: string
          payment_status: string
          payment_method: string | null
          shipping_address: any
          shipping_company: string | null
          tracking_number: string | null
          delivered_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          user_name: string
          user_email: string
          user_phone?: string | null
          order_number: string
          subtotal?: number
          shipping_fee?: number
          discount?: number
          points_used?: number
          total_amount: number
          coupon_id?: string | null
          coupon_code?: string | null
          status?: string
          payment_status?: string
          payment_method?: string | null
          shipping_address?: any
          shipping_company?: string | null
          tracking_number?: string | null
          delivered_at?: string | null
        }
        Update: {
          status?: string
          payment_status?: string
          payment_method?: string | null
          shipping_company?: string | null
          tracking_number?: string | null
          delivered_at?: string | null
        }
      }
      order_items: {
        Row: {
          id: string
          order_id: string
          product_id: string
          product_name: string
          manufacturer: string | null
          image: string | null
          quantity: number
          pill_quantity: number | null
          price: number
          total_price: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          order_id: string
          product_id: string
          product_name: string
          manufacturer?: string | null
          image?: string | null
          quantity: number
          pill_quantity?: number | null
          price: number
          total_price: number
        }
        Update: {
          quantity?: number
          price?: number
          total_price?: number
        }
      }
      crypto_payments: {
        Row: {
          id: string
          order_id: string
          provider: 'btcpay' | 'shkeeper'
          provider_invoice_id: string
          coin: string
          network: string | null
          payment_address: string
          amount_crypto: number
          amount_fiat: number
          exchange_rate: number | null
          status: 'pending' | 'partially_paid' | 'paid' | 'confirmed' | 'expired' | 'invalid'
          confirmations: number
          required_confirmations: number
          expires_at: string
          paid_at: string | null
          tx_hash: string | null
          webhook_data: any
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          order_id: string
          provider: 'btcpay' | 'shkeeper'
          provider_invoice_id: string
          coin: string
          network?: string | null
          payment_address: string
          amount_crypto: number
          amount_fiat: number
          exchange_rate?: number | null
          status?: 'pending' | 'partially_paid' | 'paid' | 'confirmed' | 'expired' | 'invalid'
          confirmations?: number
          required_confirmations?: number
          expires_at: string
          paid_at?: string | null
          tx_hash?: string | null
          webhook_data?: any
        }
        Update: {
          status?: 'pending' | 'partially_paid' | 'paid' | 'confirmed' | 'expired' | 'invalid'
          confirmations?: number
          paid_at?: string | null
          tx_hash?: string | null
          webhook_data?: any
        }
      }
      point_transactions: {
        Row: {
          id: string
          user_id: string
          amount: number
          type: 'earn' | 'use'
          description: string
          order_id: string | null
          created_at: string
        }
        Insert: {
          user_id: string
          amount: number
          type: 'earn' | 'use'
          description: string
          order_id?: string | null
        }
        Update: {
          description?: string
        }
      }
      coupons: {
        Row: {
          id: string
          code: string
          name: string
          discount_type: 'percent' | 'fixed'
          discount_value: number
          min_order_amount: number
          max_discount_amount: number | null
          usage_limit: number | null
          used_count: number
          is_active: boolean
          expires_at: string | null
          created_at: string
        }
        Insert: {
          code: string
          name: string
          discount_type: 'percent' | 'fixed'
          discount_value: number
          min_order_amount?: number
          max_discount_amount?: number | null
          usage_limit?: number | null
          is_active?: boolean
          expires_at?: string | null
        }
        Update: {
          name?: string
          discount_value?: number
          min_order_amount?: number
          max_discount_amount?: number | null
          usage_limit?: number | null
          used_count?: number
          is_active?: boolean
          expires_at?: string | null
        }
      }
      user_coupons: {
        Row: {
          id: string
          user_id: string
          coupon_id: string
          is_used: boolean
          used_at: string | null
          order_id: string | null
          created_at: string
        }
        Insert: {
          user_id: string
          coupon_id: string
          is_used?: boolean
          used_at?: string | null
          order_id?: string | null
        }
        Update: {
          is_used?: boolean
          used_at?: string | null
          order_id?: string | null
        }
      }
      reviews: {
        Row: {
          id: string
          user_id: string
          product_id: string
          order_id: string | null
          rating: number
          title: string | null
          content: string
          images: string[]
          is_verified: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          user_id: string
          product_id: string
          order_id?: string | null
          rating: number
          title?: string | null
          content: string
          images?: string[]
          is_verified?: boolean
        }
        Update: {
          rating?: number
          title?: string | null
          content?: string
          images?: string[]
        }
      }
      categories: {
        Row: {
          id: number
          name: string
          parent_id: number | null
          level: number
          created_at: string
          updated_at: string
        }
        Insert: {
          name: string
          parent_id?: number | null
          level: number
        }
        Update: {
          name?: string
          parent_id?: number | null
          level?: number
        }
      }
    }
  }
}